import React, { useState, useEffect, useMemo } from "react";
import {
    useTable,
    useSortBy,
    usePagination,
} from "react-table";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faEdit, faEllipsisV, faLink, faPlus } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from 'sweetalert2';

const InventoryMasterListing = ({ permissions, mmsPermissions }) => {
    const navigate = useNavigate();
    const code = localStorage.getItem("code");
    const token = localStorage.getItem("token");
    const name = localStorage.getItem('name');
    const role = localStorage.getItem('role');
    const badgeId = localStorage.getItem('badgeId');

    // Filters & search persistence using sessionStorage
    const [filters, setFilters] = useState(() => {
        const saved = sessionStorage.getItem("inventoryFilters");
        return saved ? JSON.parse(saved) : { group: null, type: null, createdBy: null, status: null };
    });

    const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem("inventorySearch") || "");
    const [pageIndex, setPageIndex] = useState(() => {
        const saved = sessionStorage.getItem("inventoryPage");
        return saved ? Number(saved) : 0;
    });

    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemGroups, setItemGroups] = useState([]);
    const [createdByOptions, setCreatedByOptions] = useState([]);

    // Fetch all items
    const fetchItems = async () => {
        try {
            const response = await apiClient.get(SERVER_BASE + "/get-items-all", {
                params: { centerCode: code },
                headers: { Authorization: `Bearer ${token}` },
            });
            setItems(response.data);
            // Apply initial filters if any
            applyFilters(response.data, filters, searchTerm);

            // Extract unique CreatedBy options
            const uniqueCreators = Array.from(
                new Map(
                    response.data
                        .filter((it) => it.createdByFirstName)
                        .map((it) => [
                            it.createdByBadge,
                            {
                                value: it.createdByBadge,
                                label: `${it.createdByFirstName} (${it.createdByBadge})`,
                            },
                        ])
                ).values()
            );
            setCreatedByOptions(uniqueCreators);
        } catch (error) {
            console.error("Error fetching items:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch item groups
    const fetchItemGroups = async () => {
        try {
            const response = await apiClient.get(SERVER_BASE + "/item-groups", {
                params: { code },
                headers: { Authorization: `Bearer ${token}` },
            });
            setItemGroups(
                response.data.map((group) => ({
                    value: group.groupName,
                    label: `${group.groupCode} - ${group.groupName}`,
                    type: group.type,
                    itemGroupCode: group.groupCode,
                }))
            );
        } catch (error) {
            console.error("Error fetching item groups:", error);
        }
    };

    useEffect(() => {
        fetchItems();
        fetchItemGroups();
    }, []);

    // Function to filter items
    const applyFilters = (data, filtersObj, search) => {
        let filtered = [...data];
        if (filtersObj.group) filtered = filtered.filter(item => item.itemGroup === filtersObj.group.value);
        if (filtersObj.type) filtered = filtered.filter(item => item.type === filtersObj.type.value);
        if (filtersObj.createdBy) filtered = filtered.filter(item => item.createdByBadge === filtersObj.createdBy.value);
        if (filtersObj.status) filtered = filtered.filter(item => item.status === filtersObj.status.value);
        if (search) {
            filtered = filtered.filter(item =>
                Object.values(item).join(" ").toLowerCase().includes(search.toLowerCase())
            );
        }
        setFilteredItems(filtered);
    };

    // Update filteredItems whenever filters or search change
    useEffect(() => {
        sessionStorage.setItem("inventoryFilters", JSON.stringify(filters));
        sessionStorage.setItem("inventorySearch", searchTerm);
        // Reset to page 1
        setPageIndex(0);
        applyFilters(items, filters, searchTerm);
    }, [filters, searchTerm, items]);

    // Static filter options
    const typeOptions = [
        { value: "Asset", label: "Asset / Capital Nature" },
        { value: "Consumables", label: "Useable / Consumables" },
        { value: "Fixtures & Fittings", label: "Fixtures & Fittings" },
        { value: "Constructed Structure", label: "Constructed Structure" },
    ];

    const statusOptions = [
        { value: "Final", label: "Final" },
        { value: "Draft", label: "Draft" },
        { value: "On Hold", label: "On Hold" },
    ];
    const handleEdit = (item) => {
        navigate('../itemMaster', { state: { item: item } });
    };
    const handleMMSMapping = (item) => {
        navigate('../MMSMappingForm', { state: { item: item } });
      };
    const handleAddDepartment = (item) => {
        navigate('../itemMaster', { state: { item: item, onlyDept: true } });
    };
    const handleClone = (item) => {
        navigate('../itemMaster', { state: { cloneItem: item } });
    };
    // Table columns (same as before)
    const columns = useMemo(
        () => [
            { Header: "Print Name", accessor: "printName" },
            { Header: "UOM", accessor: "uom" },
            { Header: "Weight", accessor: "avgWeight" },
            { Header: "Alias", accessor: "additionalNames" },
            { Header: "Depts", accessor: "concernedDepartments" },
            { Header: "Description", accessor: "itemDesc" },
            { Header: "Type", accessor: "type" },
            {
                Header: "Group",
                accessor: "itemGroupCode",
                Cell: ({ row }) => {
                    const { itemGroupCode, itemGroup } = row.original;
                    return `${itemGroupCode || ""} - ${itemGroup || ""}`;
                },
            },
            { Header: "Status", accessor: "status" },
            { Header: "Reason", accessor: "rejectedReason" },
            {
                Header: "Action",
                Cell: ({ row }) => {
                    const { createdByFirstName, createdAt, status, mmsName, mmsCode } = row.original;
                    const item = row.original;
                    const [suggestionData, setSuggestionData] = useState({
                        inventoryName: "",
                        size: "",
                        color: "",
                        madeOf: "",
                        grade: "",
                        brandName: "",
                        avgWeight: "",
                        uom: "",
                        remarks: "",
                    });
                    const [isSubmitting, setIsSubmitting] = useState(false);
                    const [errors, setErrors] = useState({});

                    const handleInputChange = (e) => {
                        const { name, value } = e.target;
                        setSuggestionData(prev => ({ ...prev, [name]: value }));
                        if (errors.form) {
                            setErrors(prev => ({ ...prev, form: "" }));
                        }
                    };

                    const validateForm = () => {
                        // Check if at least one field has value (excluding empty strings)
                        const hasValue = Object.values(suggestionData).some(
                            val => val && val.toString().trim() !== ""
                        );

                        if (!hasValue) {
                            setErrors({ form: "Please fill at least one field" });
                            return false;
                        }
                        setErrors({});
                        return true;
                    };

                    const handleSuggestionSubmit = async () => {
                        if (!validateForm()) return;

                        setIsSubmitting(true);

                        const payload = {
                            itemId: item.id,
                            ...suggestionData,
                            createdAt: new Date().toISOString(),
                            createdBy: name,
                            createdByBadge: badgeId,
                            status: "Suggested",
                        };

                        try {
                            await apiClient.post(`${SERVER_BASE}/saveInventorySuggestion`, payload);
                            Swal.fire({
                                title: "Success!",
                                text: "Suggestion submitted",
                                icon: "success",
                                timer: 1500,
                                showConfirmButton: false
                            });
                            document.getElementById(`closeModal-${item.id}`)?.click();
                        } catch (err) {
                            Swal.fire({
                                title: "Error!",
                                text: err.response?.data?.message || "Submission failed",
                                icon: "error",
                            });
                        } finally {
                            setIsSubmitting(false);
                        }
                    };
                    return (
                        <div className="dropdown text-center">
                            <button
                                className="btn btn-light btn-sm border"
                                type="button"
                                id={`dropdownMenuButton-${row.id}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                            <ul
                                className="dropdown-menu"
                                aria-labelledby={`dropdownMenuButton-${row.id}`}
                            >
                                {((permissions?.edit || role == 'itManager') && (status != "Final")) && (
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleEdit(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                            Edit
                                        </button>
                                    </li>
                                )}
                                {((permissions?.write || role == 'itManager') && (status != "On Hold")) && (
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleClone(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faCopy} className="me-2" />
                                            Clone
                                        </button>
                                    </li>
                                )}
                                {((permissions?.edit || permissions?.write || role == 'itManager') && (status == "Final")) && (
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleAddDepartment(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Add Department
                                        </button>
                                    </li>
                                )}
                                {((permissions?.write || role == 'itManager') && (status == "Final")) && (
                                    <li>
                                        <button
                                            className="dropdown-item py-1"
                                            data-bs-toggle="modal"
                                            data-bs-target={`#suggestModal-${item.id}`}
                                        >
                                            <FontAwesomeIcon icon={faEdit} className="me-2 fs-6" />
                                            Edit Suggestion
                                        </button>
                                    </li>
                                )}
                                {((mmsPermissions?.write &&
                                    status === "Final" &&
                                    !item.mmsCode &&
                                    !item.mmsName) || role === "itManager") && (
                                        <li>
                                           <button
                                            className="dropdown-item"
                                            onClick={() => handleMMSMapping(row.original)}
                                        >
                                                <FontAwesomeIcon icon={faLink} className="me-2 fs-6" />
                                                MMS Mapping
                                            </button>
                                        </li>
                                    )}

                                {createdByFirstName && (
                                    <li className="dropdown-item">
                                        <FontAwesomeIcon icon={faPlus} /> {createdByFirstName}
                                        {createdAt &&
                                            ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                                    </li>
                                )}
                            </ul>
                            <div
                                className="modal fade"
                                id={`suggestModal-${item.id}`}
                                tabIndex="-1"
                                aria-labelledby="suggestModalLabel"
                                aria-hidden="true"
                            >
                                <div className="modal-dialog modal-lg">
                                    <div className="modal-content" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                                        <div className="modal-header p-2 bg-light">
                                            <h5 className="modal-title fs-6 m-0">Edit Suggestion</h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                data-bs-dismiss="modal"
                                                aria-label="Close"
                                                id={`closeModal-${item.id}`}
                                            ></button>
                                        </div>
                                        <div className="modal-body p-3">
                                            {errors.form && (
                                                <div className="alert alert-warning py-1 mb-2 small">
                                                    {errors.form}
                                                </div>
                                            )}

                                            <div className="row g-1 row-cols-2 row-cols-md-3">
                                                {[
                                                    { label: "Name", name: "inventoryName", cols: 2 },
                                                    { label: "Size", name: "size", cols: 1 },
                                                    { label: "Color", name: "color", cols: 1 },
                                                    { label: "Material", name: "madeOf", cols: 1 },
                                                    { label: "Grade", name: "grade", cols: 1 },
                                                    { label: "Brand", name: "brandName", cols: 2 },
                                                    { label: "Weight", name: "avgWeight", cols: 1 },
                                                    { label: "UOM", name: "uom", cols: 1 },
                                                ].map((field) => (
                                                    <div className={`col mb-1`} key={field.name}>
                                                        <label className="form-label mb-0">
                                                            {field.label}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control"
                                                            name={field.name}
                                                            value={suggestionData[field.name]}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="mt-2">
                                                <label className="form-label mb-0">Remarks</label>
                                                <textarea
                                                    className="form-control form-control"
                                                    name="remarks"
                                                    rows={2}
                                                    value={suggestionData.remarks}
                                                    onChange={handleInputChange}
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="modal-footer p-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                data-bs-dismiss="modal"
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={handleSuggestionSubmit}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                                        Saving
                                                    </>
                                                ) : (
                                                    "Submit"
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        ],
        []
    );

    // Table instance
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,   // <-- this is required
        state: tableState,
    } = useTable(
        {
            columns,
            data: filteredItems,
            initialState: { pageIndex: pageIndex, pageSize: 25 }, // default 25
        },
        useSortBy,
        usePagination
    );


    // Sync table pageIndex with sessionStorage
    useEffect(() => {
        setPageIndex(tableState.pageIndex);
        sessionStorage.setItem("inventoryPage", tableState.pageIndex);
    }, [tableState.pageIndex]);

    return (
        <div className="formcontainer mt-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Inventory Master</h4>
            </div>

            {(permissions?.write || role === 'itManager') && (
                <button
                    className="btn btn-outline-success mb-2"
                    onClick={() => navigate('../itemMaster')}
                >
                    Add Inventory
                </button>
            )}

            {/* Search + Filters + Count */}
            <div className="card p-3 mb-3 shadow-sm">
                <div className="row g-3 align-items-end">
                    {/* Search */}
                    <div className="col-md-6">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Item Group */}
                    <div className="col-md-4">
                        <Select
                            options={itemGroups}
                            value={filters.group}
                            onChange={(selected) => setFilters({ ...filters, group: selected })}
                            placeholder='Item Group'
                            isClearable
                        />
                    </div>

                    {/* Page Size */}
                    <div className="col-md-2">
                        <select
                            className="form-select"
                            value={tableState?.pageSize || 10}
                            onChange={(e) => {
                                const newSize = Number(e.target.value);
                                setPageSize(newSize);
                            }}
                        >
                            {[...Array(8)].map((_, idx) => {
                                const val = (idx + 1) * 25; // 25, 50, 75, ..., 200
                                return (
                                    <option key={val} value={val}>
                                        {val} / page
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Type Filter */}
                    <div className="col-md-3">
                        <Select
                            options={typeOptions}
                            value={filters.type}
                            onChange={(selected) => setFilters({ ...filters, type: selected })}
                            placeholder='Type'
                            isClearable
                        />
                    </div>

                    {/* Created By Filter */}
                    <div className="col-md-3">
                        <Select
                            options={createdByOptions}
                            value={filters.createdBy}
                            onChange={(selected) => setFilters({ ...filters, createdBy: selected })}
                            placeholder='Created By'
                            isClearable
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="col-md-3">
                        <Select
                            options={statusOptions}
                            value={filters.status}
                            onChange={(selected) => setFilters({ ...filters, status: selected })}
                            placeholder='Status'
                            isClearable
                        />
                    </div>

                    {/* Record Count */}
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control text-center fw-semibold"
                            value={`Showing ${filteredItems.length} of ${items.length} records`}
                            readOnly
                        />
                    </div>
                </div>
            </div>


            {/* Table */}
            {loading ? (
                <div className="text-center my-5">Loading items...</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-striped table-hover" {...getTableProps()}>
                        <thead className="table-light" style={{ fontSize: '15px' }}>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render("Header")}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? " 🔽"
                                                        : " 🔼"
                                                    : ""}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()} style={{ fontSize: '13px', verticalAlign: 'baseline' }}>
                            {page.length > 0 ? (
                                page.map(row => {
                                    prepareRow(row);

                                    // Get the status from the row
                                    const status = row.original.status;
                                    let rowClass = "";
                                    if (status === "Final") rowClass = "text-success";
                                    else if (status === "Draft") rowClass = "text-primary";
                                    else if (status === "On Hold") rowClass = "text-danger";

                                    return (
                                        <tr {...row.getRowProps()} >
                                            {row.cells.map(cell => (
                                                <td className={rowClass} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center py-3">
                                        No records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            )}

            {/* Pagination */}
            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                {/* Previous on left */}
                <div>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                    >
                        Previous
                    </button>
                </div>

                {/* Page info in center */}
                <div className="text-center flex-grow-1">
                    <span>
                        Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
                    </span>
                </div>

                {/* Next on right */}
                <div className="text-end">
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                    >
                        Next
                    </button>
                </div>
            </div>

        </div>
    );
};

export default InventoryMasterListing;
